// ============================================================================
// Signal tab — the learning loop, honest about data. 2026-07-02.
//
// Reads sourcing_signal (facility_feedback → service_assignments attribution,
// per provider AND per individual cleaner). Every row carries sample_size +
// confidence; sparse data renders a plain "not enough feedback data yet"
// state instead of a fabricated score.
//
// Guardrails baked in (Jack's HTML §D):
//   • whole-sample weighting — scores derive from all attributed events
//   • minimum evidence — low-N rows are visually de-emphasized and labeled
//   • diagnose, don't blacklist — no row is ever hidden or flagged "bad" on
//     thin data; the UI shows the breakdown and lets a human read it
// ============================================================================

import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { EmptyState, Skeleton } from '../../shared/components';
import type { SignalRow } from './hooks/use-sourcing-data';
import type { ProviderOption } from './hooks/use-sourcing-data';

// Feedback logging is ~6% of visits today (2026-07). Below this many total
// events the whole tab leads with the seeding banner.
const SEEDING_THRESHOLD = 30;

const CONF_STYLES: Record<SignalRow['confidence'], string> = {
  high: 'bg-st-ok-bg text-st-ok-fg',
  med: 'bg-st-warn-bg text-st-warn-fg',
  low: 'bg-tcs-surface text-tcs-text-muted',
};

function scoreTone(s: SignalRow): string {
  if (s.confidence === 'low') return 'text-tcs-text-muted';
  if (s.signal_score >= 67) return 'text-st-ok-fg';
  if (s.signal_score >= 50) return 'text-st-warn-fg';
  return 'text-st-danger-fg';
}

export function SignalTab({
  rows,
  isLoading,
  providers,
}: {
  rows: SignalRow[];
  isLoading: boolean;
  providers: ProviderOption[];
}) {
  const [showWorkers, setShowWorkers] = useState(true);

  const nameOf = useMemo(() => {
    const m = new Map(providers.map((p) => [p.provider_id, p.partner_name]));
    return (id: string) => m.get(id) ?? id;
  }, [providers]);

  const providerRows = useMemo(
    () => rows.filter((r) => r.worker_name === null).sort((a, b) => b.sample_size - a.sample_size),
    [rows],
  );
  const workerRows = useMemo(
    () => rows.filter((r) => r.worker_name !== null).sort((a, b) => b.sample_size - a.sample_size),
    [rows],
  );
  const totalEvents = useMemo(
    () => providerRows.reduce((acc, r) => acc + r.sample_size, 0),
    [providerRows],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-2/3" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title="Not enough feedback data yet"
        body="The signal loop reads facility feedback attributed to subs and individual cleaners. Nothing is attributed yet — this will sharpen as AMs log feedback."
      />
    );
  }

  return (
    <div className="space-y-4">
      {totalEvents < SEEDING_THRESHOLD && (
        <div className="flex items-start gap-2 rounded-lg border border-st-warn-border bg-st-warn-bg p-3 text-sm text-st-warn-fg-strong">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>Seeding — thin data.</b> Only {totalEvents} attributed feedback events so far
            (feedback logging is ~6% of visits). Scores below are directional, not verdicts;
            they will sharpen as AMs log feedback. Low-N rows never drive a blacklist —
            diagnose, don't blanket-penalize.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-tcs-text-muted">
          Signal score 0–100 from attributed feedback (Positive +, Negative −, Critical −−),
          weighted across the whole sample. Confidence: high ≥20 events · med ≥8 · low &lt;8.
        </p>
        <label className="flex shrink-0 items-center gap-1.5 text-xs text-tcs-text-muted">
          <input
            type="checkbox"
            checked={showWorkers}
            onChange={(e) => setShowWorkers(e.target.checked)}
          />
          Show individual cleaners
        </label>
      </div>

      <SignalTable
        title={`Providers (${providerRows.length})`}
        rows={providerRows}
        who={(r) => nameOf(r.provider_id)}
      />

      {showWorkers && (
        <SignalTable
          title={`Individual cleaners (${workerRows.length})`}
          rows={workerRows}
          who={(r) => `${r.worker_name} — ${nameOf(r.provider_id)}`}
        />
      )}
    </div>
  );
}

function SignalTable({
  title,
  rows,
  who,
}: {
  title: string;
  rows: SignalRow[];
  who: (r: SignalRow) => string;
}) {
  if (rows.length === 0) {
    return (
      <section>
        <h3 className="mb-1 text-sm font-semibold text-tcs-text">{title}</h3>
        <p className="text-sm text-tcs-text-muted">
          Not enough feedback data yet — no attributed events at this grain.
        </p>
      </section>
    );
  }
  return (
    <section>
      <h3 className="mb-1 text-sm font-semibold text-tcs-text">{title}</h3>
      <div className="overflow-x-auto rounded-lg border border-tcs-border">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-tcs-border text-[10px] uppercase tracking-wide text-tcs-text-muted">
              <th className="px-3 py-2">Who</th>
              <th className="px-3 py-2 text-right">Signal</th>
              <th className="px-3 py-2 text-right">Sample</th>
              <th className="px-3 py-2">Confidence</th>
              <th className="px-3 py-2">Breakdown (+ / ○ / − / crit)</th>
              <th className="px-3 py-2">Last feedback</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.provider_id}-${r.worker_name ?? '∅'}`}
                className={`border-b border-tcs-border/60 last:border-0 ${
                  r.confidence === 'low' ? 'opacity-70' : ''
                }`}
              >
                <td className="px-3 py-2 font-medium text-tcs-text">{who(r)}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${scoreTone(r)}`}>
                  {r.signal_score}
                  {r.confidence === 'low' && (
                    <span className="ml-1 text-[10px] font-normal text-tcs-text-muted">low N</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{r.sample_size}</td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${CONF_STYLES[r.confidence]}`}
                  >
                    {r.confidence}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.positive_count} / {r.neutral_count} / {r.negative_count} / {r.critical_count}
                </td>
                <td className="px-3 py-2 text-xs text-tcs-text-muted">
                  {r.last_feedback_date ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
